import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';
import { motion } from 'framer-motion';
import DiscordButton from '@common/layout/DiscordButton';
import useUsers from '@modules/users/hooks/api/useUsers';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { CONTENT_PADDING_X, MAX_CONTENT_WIDTH } from '@common/defs/layout';

interface WaitlistBannerProps {}
export const BANNER_HEIGHT = 55;
const WaitlistBanner = (_props: WaitlistBannerProps) => {
  const { user } = useAuth();
  const { waitlistData } = useUsers();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  if (!user) {
    return null;
  }
  const referralLink = process.env.NEXT_PUBLIC_APP_URL + '?ref=' + user.id * 157;
  const handleCopyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      // Ignore copy errors silently for now
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const discordInviteLink = process.env.NEXT_PUBLIC_DISCORD_JOIN_URL;

  if (!waitlistData) {
    return null;
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          top: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          width: '100%',
          color: 'common.white',
          height: `${BANNER_HEIGHT}px`,
        }}
      >
        <Box
          sx={{
            maxWidth: MAX_CONTENT_WIDTH + 'px',
            paddingX: CONTENT_PADDING_X,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 1,
          }}
        >
          <Typography variant="body2" fontSize={13} fontWeight={400}>
            {waitlistData.showUsersInRegion && (
              <>
                {waitlistData.usersInRegion}/{waitlistData.waitlistMax} inscrits.
              </>
            )}{' '}
            Encore {waitlistData.waitlistMax - waitlistData.usersInRegion} places
          </Typography>
          <Button
            onClick={handleClickOpen}
            variant="outlined"
            sx={{
              // make it white
              color: 'common.white',
              borderColor: 'common.white',
              fontWeight: 400,
              // width: '250px',
              // no return line text
              textWrap: 'nowrap',
              px: 4,
              '&:hover': {
                borderColor: 'common.white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            En savoir +
          </Button>
        </Box>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="waitlist-dialog-title"
        fullScreen={isMobile}
      >
        <DialogTitle id="waitlist-dialog-title">
          {waitlistData.isOpenedRegion
            ? 'Le volume de demande augmentera fortement dès que le nombre minimum de praticiens est atteint.'
            : 'L’ouverture de votre zone aura lieu dès que le nombre minimum de praticiens est atteint.'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 5 }}>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              {waitlistData.isOpenedRegion
                ? 'Chaque inscription compte : plus il y a de praticiens dans une zone, plus vite le flux de clients peut augmenter.'
                : 'Chaque inscription compte : plus vite le nombre est atteint, plus vite l’ouverture peut avoir lieu.'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5, fontWeight: 600 }}>
              Chaque nouvelle inscription :
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              {waitlistData.isOpenedRegion
                ? '➝ augmente le volume de demandes envoyé,'
                : '➝ accélère l’ouverture de la région,'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2.5 }}>
              ➝ améliore le matching.
            </Typography>
          </Box>

          {/* Jauge de progression avec animation (valeurs factices) */}

          {waitlistData.showUsersInRegion && (
            <Box sx={{ mb: 5 }}>
              {/* <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Objectif de praticiens dans votre région
            </Typography> */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {waitlistData.usersInRegion} praticiens inscrits
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Objectif&nbsp;: {waitlistData.waitlistMax}
                  </Typography>
                </Box>
                <Box
                  component={motion.div}
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={(waitlistData.usersInRegion / waitlistData.waitlistMax) * 100}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Bloc parrainage */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 500 }}>
              {waitlistData.isOpenedRegion
                ? 'Invitez des collègues pour accélérer les demandes dans votre région.'
                : 'Invitez des collègues pour accélérer l’ouverture dans votre région.'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Partagez votre lien :
            </Typography>
            <Box
              onClick={handleCopyReferralLink}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                backgroundColor: 'grey.50',
                mb: 1.5,
                wordBreak: 'break-all',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'grey.100',
                  borderColor: 'grey.400',
                },
              }}
            >
              <Typography variant="body2" sx={{ mr: 1, flex: 1 }}>
                {referralLink}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <ContentCopyIcon fontSize="small" color={copied ? 'action' : 'action'} />
                {copied && <Typography variant="caption">Copié !</Typography>}
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mb: 4 }}>
              Pour chaque collègue inscrit, nous vous{' '}
              <strong>réservons 3 clients en priorité</strong>.
            </Typography>
            {discordInviteLink && (
              <DiscordButton text="Rejoindre la communauté" />
              // <Button
              //   href={discordInviteLink}
              //   target="_blank"
              //   rel="noopener noreferrer"
              //   variant="text"
              //   fullWidth
              // >
              //   Rejoindre la communauté Discord
              // </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WaitlistBanner;
